import dayjs from 'dayjs';

import { convertTemperature } from '../../../lib/convert';
import type { RecordFilters } from '../../shared';

const DEFAULT_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

function formatDuration(length: any, interval: any, translation: any) {
    const totalMinutes = Math.floor((length * interval) / 60);
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60) % 24;
    const days = Math.floor(totalMinutes / (60 * 24));

    return `${days} ${translation.D} ${hours} ${translation.H} ${minutes} ${translation.M}`;
}

function formatWithOffset(date: any, offsetMinutes: any, format: any = DEFAULT_TIME_FORMAT) {
    return dayjs(date).add(offsetMinutes, 'm').format(format);
}

function mapRecordToRow(record: any, options: any) {
    const { translation, tempUnit, index, timeOffset, now = dayjs() } = options;
    const labelIds = Array.isArray(record.label_ids)
        ? record.label_ids
        : Array.isArray(record.labelIds)
          ? record.labelIds
          : Array.isArray(record.labels)
            ? record.labels.map((label: any) => label?.id || label?.label_id || label?.labelId).filter(Boolean)
            : [];

    return {
        num: index,
        id: `${record.device_id}-${record.index}`,
        createTime: formatWithOffset(record.create_time, timeOffset),
        startTime: formatWithOffset(record.start_time, timeOffset),
        endTime: formatWithOffset(record.end_time, timeOffset),
        length: record.number_of_records,
        duration: formatDuration(record.number_of_records, record.interval, translation),
        interval: record.interval,
        timeZone: dayjs(now).format('UTCZ'),
        company: record.meta_data?.shipper_name,
        status: record.is_normal,
        trackNum: record.meta_data?.shipment_number,
        note: record.meta_data?.note,
        deviceId: record.device_id,
        index: record.index,
        lowerLimit: convertTemperature(record.threshold?.lower_limit, tempUnit),
        upperLimit: convertTemperature(record.threshold?.upper_limit, tempUnit),
        timeLimit: record.threshold?.time_limit,
        totalMinutes: record.total_minute,
        thresholdType: record.threshold?.type,
        labelIds,
        metaData: record.meta_data || {},
        threshold: record.threshold || {},
        notifyEmails: record.notify_emails || [],
        modelInfo: record.model_info || {},
    };
}

function createDataChildren(time: any, temperature: any) {
    return { time, temperature };
}

function filterRecords(records: any, keyword: any, filters: any = {}) {
    const { statusFilter = 'any', deviceTypeFilter = 'all', durationRange, labelFilter = [] } = filters;

    return records.filter((record: any) => {
        if (keyword && !(record.deviceId ? record.deviceId.includes(keyword) : false)) {
            return false;
        }

        if (statusFilter !== 'any') {
            const isNormal = record.status === true || record.status === 1 || record.status === 'normal';
            const isAbnormal = record.status === false || record.status === 0 || record.status === 'abnormal';
            if (statusFilter === 'pass' && !isNormal) return false;
            if (statusFilter === 'fail' && !isAbnormal) return false;
        }

        if (deviceTypeFilter !== 'all') {
            const model = (record.modelInfo?.model || '').toString().toLowerCase();
            const isLite = model.includes('lite');
            const isReuse = model.includes('reuse') || model.includes('re-use') || model.includes('re use');
            const isCotag = model.includes('cotag');

            if (deviceTypeFilter === 'cotag' && !(isCotag && !isLite && !isReuse)) return false;
            if (deviceTypeFilter === 'cotagLite' && !isLite) return false;
            if (deviceTypeFilter === 'cotagReuse' && !isReuse) return false;
        }

        if (Array.isArray(durationRange) && durationRange.length === 2) {
            const [minDays, maxDays] = durationRange;
            const totalMinutes =
                record.totalMinutes ??
                (record.length && record.interval ? (record.length * record.interval) / 60 : null);
            const durationDays = totalMinutes !== null ? totalMinutes / (60 * 24) : null;
            if (durationDays === null) return false;
            const maxBound = Number.isFinite(maxDays) ? maxDays : Number.POSITIVE_INFINITY;
            if (durationDays < minDays || durationDays > maxBound) return false;
        }

        if (Array.isArray(labelFilter) && labelFilter.length > 0) {
            const recordLabelIds = Array.isArray(record.labelIds) ? record.labelIds : [];
            const hasLabel = recordLabelIds.some((id: any) => labelFilter.includes(id));
            if (!hasLabel) return false;
        }

        return true;
    });
}

type RecordRow = ReturnType<typeof mapRecordToRow>;

// filterRecords with the shared RecordFilters shape that useRecordFilters produces.
const filterRecordRows = (rows: RecordRow[], filters: RecordFilters): RecordRow[] =>
    filterRecords(rows, filters.search, {
        statusFilter: filters.status,
        deviceTypeFilter: filters.deviceType,
        durationRange: filters.durationRange,
        labelFilter: filters.labels,
    });

export type { RecordRow };
export { createDataChildren, filterRecordRows, filterRecords, formatDuration, formatWithOffset, mapRecordToRow };
